import type { ReactNode } from "react";
import { ScrollReveal } from "./ScrollReveal";

export type PageHeroVariant =
  | "about"
  | "services"
  | "checkin"
  | "partnerships"
  | "careers"
  | "contact";

/** Bold-line flourishes, one per page, all drawn on a 520×260 canvas. */
const DOODLES: Record<PageHeroVariant, ReactNode> = {
  /* A dip followed by a climb: learning from setbacks. */
  about: (
    <>
      <path
        className="gw-doodle gw-doodle--draw gw-doodle--sky"
        pathLength={1}
        d="M36 214C86 206 118 152 160 150C202 148 214 200 178 206C142 212 140 160 192 130C244 100 292 88 342 60"
      />
      <path className="gw-doodle gw-doodle--late gw-doodle--sky" d="M317 51l25 9-11 23" />
      <path
        className="gw-doodle gw-doodle--draw gw-doodle--blue"
        pathLength={1}
        d="M282 206q15-18 30 0t30 0 30 0 30 0"
      />
      <path className="gw-doodle gw-doodle--pale" d="M424 40q3 27 26 30-23 3-26 30-3-27-26-30 23-3 26-30Z" />
      <path
        className="gw-doodle gw-doodle--faint"
        d="M478 168c-16-6-36 6-34 24s26 26 40 14 8-36-10-38c-14-2-24 6-26 14"
      />
      <path className="gw-doodle gw-doodle--sky gw-doodle--thin" d="M200 246l12-14 12 14 12-14 12 14" />
      <circle className="gw-doodle-dot" cx="252" cy="40" r="6" />
      <circle className="gw-doodle-dot" cx="500" cy="112" r="5" />
      <circle className="gw-doodle-dot" cx="92" cy="96" r="4" />
    </>
  ),

  /* Steps up to a flag, with an open book: growing through every stage of life. */
  services: (
    <>
      <path
        className="gw-doodle gw-doodle--draw gw-doodle--sky"
        pathLength={1}
        d="M40 232H104V194H168V156H232V118H296V80H350"
      />
      <path className="gw-doodle gw-doodle--late gw-doodle--sky gw-doodle--thin" d="M350 80V24" />
      <path className="gw-doodle gw-doodle--late gw-doodle--pale" d="M350 26l38 12-38 13" />
      <circle className="gw-doodle-dot" cx="136" cy="176" r="5" />
      <circle className="gw-doodle-dot" cx="200" cy="138" r="5" />
      <circle className="gw-doodle-dot" cx="264" cy="100" r="5" />
      <path
        className="gw-doodle gw-doodle--faint"
        d="M412 186q24-14 44 0q20-14 44 0v42q-20-14-44 0q-20-14-44 0Z M456 186v42"
      />
      <path className="gw-doodle gw-doodle--pale" d="M462 66q2 19 18 21-16 2-18 21-2-19-18-21 16-2 18-21Z" />
      <path
        className="gw-doodle gw-doodle--draw gw-doodle--blue gw-doodle--thin"
        pathLength={1}
        d="M48 150q12-14 24 0t24 0 24 0"
      />
      <circle className="gw-doodle-dot" cx="400" cy="118" r="4" />
    </>
  ),

  /* A checklist, a pulse line and a heart: checking in with yourself. */
  checkin: (
    <>
      <path
        className="gw-doodle gw-doodle--draw gw-doodle--blue"
        pathLength={1}
        d="M24 168H118L136 120L160 214L180 140L194 168H310"
      />
      <rect className="gw-doodle gw-doodle--faint" x="356" y="40" width="36" height="36" rx="9" />
      <rect className="gw-doodle gw-doodle--faint" x="356" y="108" width="36" height="36" rx="9" />
      <rect className="gw-doodle gw-doodle--faint" x="356" y="176" width="36" height="36" rx="9" />
      <path
        className="gw-doodle gw-doodle--draw gw-doodle--sky"
        pathLength={1}
        d="M364 58l9 10 18-22"
      />
      <path
        className="gw-doodle gw-doodle--draw gw-doodle--sky"
        pathLength={1}
        d="M364 126l9 10 18-22"
      />
      <path className="gw-doodle gw-doodle--pale gw-doodle--thin" d="M412 58H496M412 126H478M412 194H488" />
      <path
        className="gw-doodle gw-doodle--pale"
        d="M84 96C52 76 60 44 84 60C108 44 116 76 84 96Z"
      />
      <circle className="gw-doodle-dot" cx="236" cy="70" r="6" />
      <circle className="gw-doodle-dot" cx="300" cy="232" r="4" />
      <circle className="gw-doodle-dot" cx="160" cy="46" r="4" />
    </>
  ),

  /* Two interlocking rings joined by a dotted arc: working together. */
  partnerships: (
    <>
      <circle
        className="gw-doodle gw-doodle--draw gw-doodle--sky"
        pathLength={1}
        cx="206"
        cy="140"
        r="64"
      />
      <circle
        className="gw-doodle gw-doodle--draw gw-doodle--blue"
        pathLength={1}
        cx="298"
        cy="140"
        r="64"
      />
      <path className="gw-doodle gw-doodle--dotted" d="M150 48Q252 0 354 48" />
      <path className="gw-doodle gw-doodle--pale" d="M440 52q3 25 24 28-21 3-24 28-3-25-24-28 21-3 24-28Z" />
      <path className="gw-doodle gw-doodle--faint" d="M452 176v40M432 196h40" />
      <path className="gw-doodle gw-doodle--sky gw-doodle--thin" d="M40 222q14-16 28 0t28 0 28 0" />
      <circle className="gw-doodle-dot" cx="252" cy="140" r="6" />
      <circle className="gw-doodle-dot" cx="80" cy="90" r="4" />
      <circle className="gw-doodle-dot" cx="400" cy="232" r="5" />
    </>
  ),

  /* A winding path to a flag, with a compass star: where a career can lead. */
  careers: (
    <>
      <path
        className="gw-doodle gw-doodle--draw gw-doodle--sky"
        pathLength={1}
        d="M28 232C120 232 96 164 188 164S286 100 360 96"
      />
      <path className="gw-doodle gw-doodle--late gw-doodle--sky gw-doodle--thin" d="M378 100V30" />
      <path className="gw-doodle gw-doodle--late gw-doodle--pale" d="M378 32q22-8 42 4t40 0v34q-20 12-40 0t-42 4" />
      <path
        className="gw-doodle gw-doodle--faint"
        d="M470 150l10 30 30 10-30 10-10 30-10-30-30-10 30-10Z"
      />
      <path
        className="gw-doodle gw-doodle--draw gw-doodle--blue gw-doodle--thin"
        pathLength={1}
        d="M236 236l12-14 12 14 12-14 12 14"
      />
      <circle className="gw-doodle-dot" cx="100" cy="196" r="5" />
      <circle className="gw-doodle-dot" cx="188" cy="164" r="5" />
      <circle className="gw-doodle-dot" cx="286" cy="118" r="5" />
      <circle className="gw-doodle-dot" cx="240" cy="54" r="4" />
    </>
  ),

  /* A paper plane on a looping trail, with a chat bubble: getting in touch. */
  contact: (
    <>
      <path
        className="gw-doodle gw-doodle--draw gw-doodle--blue"
        pathLength={1}
        d="M40 214C100 214 122 156 170 164C218 172 202 222 166 208C130 194 168 134 248 122C286 116 306 106 322 98"
      />
      <path className="gw-doodle gw-doodle--sky" d="M330 92L492 32L434 140L402 108Z M492 32L402 108" />
      <path
        className="gw-doodle gw-doodle--faint"
        d="M58 40h76a14 14 0 0 1 14 14v30a14 14 0 0 1-14 14H96l-18 16V98H58a14 14 0 0 1-14-14V54a14 14 0 0 1 14-14Z"
      />
      <circle className="gw-doodle-dot" cx="80" cy="69" r="4" />
      <circle className="gw-doodle-dot" cx="96" cy="69" r="4" />
      <circle className="gw-doodle-dot" cx="112" cy="69" r="4" />
      <path className="gw-doodle gw-doodle--pale" d="M470 172q2 19 18 21-16 2-18 21-2-19-18-21 16-2 18-21Z" />
      <circle className="gw-doodle-dot" cx="300" cy="200" r="5" />
      <circle className="gw-doodle-dot" cx="226" cy="48" r="4" />
    </>
  ),
};

/** Shared dark banner at the top of each sub-page. */
export const PageHero = ({
  variant,
  eyebrow,
  title,
  subtitle,
}: {
  variant: PageHeroVariant;
  eyebrow: string;
  title: string;
  subtitle: string;
}) => (
  <section className="gw-section gw-section--ink gw-page-hero">
    <div className="gw-container">
      <svg
        className="gw-page-hero__doodle"
        viewBox="0 0 520 260"
        aria-hidden="true"
        focusable="false"
      >
        {DOODLES[variant]}
      </svg>
      <ScrollReveal className="gw-page-hero__copy">
        <p className="gw-eyebrow">{eyebrow}</p>
        <h1 className="gw-title gw-page-hero__title">{title}</h1>
        <p className="gw-subtitle">{subtitle}</p>
      </ScrollReveal>
    </div>
  </section>
);
